// crewbit-admin/src/hooks/useInvoiceDetail.ts
//
// Hook contract: docs/references/MONEY_MODULE_SPEC_v1.md §InvoiceDetail.7.1.
// Single-invoice fetch with the explicit-column SELECT the new admin slide-over
// needs. Mirrors crewbit-mobile/hooks/useInvoiceDetail.ts (Session 124) — same
// envelope, web-runtime plumbing.
//
// Refresh policy mirrors usePaymentLedger (admin):
//   - Initial mount → fetch.
//   - Page Visibility 'visible' → throttled to 30s.
//   - Cache-invalidate on MONEY_MUTATION_EVENT (window event).
//   - dataRef pattern keeps doFetch identity stable across state changes.

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { supabase } from '../lib/supabase';
import { MONEY_MUTATION_EVENT } from './useFinancialSummary';
import type { Invoice } from '../types';

const FOCUS_REFETCH_THROTTLE_MS = 30_000;

const SELECT_COLS =
  'id, business_id, client_id, work_order_id, project_id, source_quote_id, ' +
  'invoice_number, line_items, subtotal, tax_rate, tax_amount, total, status, ' +
  'due_date, sent_at, sent_via, viewed_at, paid_at, payment_method, payment_method_used, ' +
  'client_name, client_email, client_phone, client_address, ' +
  'business_name, business_address, business_phone, business_email, business_hst_number, ' +
  'share_token, stripe_payment_link_url, stripe_session_id, stripe_payment_intent_id, ' +
  'stripe_connect_account_id, stripe_application_fee_amount, ' +
  'platform_fee, notes, created_by, created_at, updated_at, ' +
  'is_sales_receipt, is_quick_bill, idempotency_key, ' +
  'cancelled_at, cancellation_reason, voided_at, ' +
  'written_off_at, written_off_reason, last_reminder_sent_at, ' +
  'refund_amount, refunded_at, last_stripe_refund_id, ' +
  'is_disputed, disputed_at, stripe_dispute_id, dispute_status, ' +
  'dispute_amount, dispute_reason, dispute_event_id';

export interface UseInvoiceDetailReturn {
  data: Invoice | null;
  isLoading: boolean;
  isRefreshing: boolean;
  error: Error | null;
  refetch: () => Promise<void>;
}

export function useInvoiceDetail(invoiceId: string | null): UseInvoiceDetailReturn {
  const [data, setData] = useState<Invoice | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefreshing, setIsRefreshing] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const dataRef = useRef<Invoice | null>(null);
  useEffect(() => { dataRef.current = data; }, [data]);

  const lastFocusFetchRef = useRef<number>(0);
  const inFlightRef = useRef<boolean>(false);
  const mountedRef = useRef<boolean>(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => { mountedRef.current = false; };
  }, []);

  const doFetch = useCallback(
    async (manual: boolean) => {
      if (!invoiceId) return;
      if (inFlightRef.current) return;
      inFlightRef.current = true;

      const isInitial = dataRef.current === null;
      if (isInitial) setIsLoading(true);
      else if (manual) setIsRefreshing(true);

      try {
        const { data: row, error: queryError } = await supabase
          .from('invoices')
          .select(SELECT_COLS)
          .eq('id', invoiceId)
          .maybeSingle();

        if (!mountedRef.current) return;

        if (queryError) {
          setError(new Error(queryError.message));
        } else {
          setData(row ? (row as unknown as Invoice) : null);
          setError(null);
        }
      } finally {
        if (mountedRef.current) {
          setIsLoading(false);
          setIsRefreshing(false);
        }
        inFlightRef.current = false;
      }
    },
    [invoiceId],
  );

  // Reset on invoiceId change.
  useEffect(() => {
    setData(null);
    setError(null);
  }, [invoiceId]);

  // Mount fetch.
  useEffect(() => {
    if (!invoiceId) return;
    void doFetch(false);
  }, [invoiceId, doFetch]);

  // Page Visibility focus refetch.
  useEffect(() => {
    if (!invoiceId) return;
    const onVisibility = () => {
      if (document.visibilityState !== 'visible') return;
      const now = Date.now();
      if (now - lastFocusFetchRef.current >= FOCUS_REFETCH_THROTTLE_MS) {
        lastFocusFetchRef.current = now;
        void doFetch(true);
      }
    };
    document.addEventListener('visibilitychange', onVisibility);
    return () => { document.removeEventListener('visibilitychange', onVisibility); };
  }, [invoiceId, doFetch]);

  // MONEY_MUTATION_EVENT cache invalidation.
  useEffect(() => {
    const onMutation = () => { if (invoiceId) void doFetch(true); };
    window.addEventListener(MONEY_MUTATION_EVENT, onMutation);
    return () => { window.removeEventListener(MONEY_MUTATION_EVENT, onMutation); };
  }, [invoiceId, doFetch]);

  const refetch = useCallback(async () => { await doFetch(true); }, [doFetch]);

  return useMemo(
    () => ({ data, isLoading, isRefreshing, error, refetch }),
    [data, isLoading, isRefreshing, error, refetch],
  );
}
